import { useMemo } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@rocco/components/dropdown-menu';
import type { MeUser } from '~/server/types';
import { useNotifications } from '~/modules/notifications';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';
import { NotificationBadge } from '../NotificationBadge';

/**
 * A dropdown menu that allows the user to switch between personal and company
 * inboxes.
 * @see {@link ChatInboxDrawer}
 */
export function ChatInboxSwitcher({
    className,
    user,
    professionalID,
    onInboxChange,
}: {
    className?: string;
    user: MeUser;
    professionalID: number | null;
    onInboxChange?: (professionalID: number | null) => void;
}) {
    const { notifications } = useNotifications();
    const companyInboxes = useMemo(() => {
        return user.Professionals.filter(
            (professional) => professional.HasInboxAccess,
        );
    }, [user.Professionals]);
    const selectedInbox = useMemo(() => {
        if (professionalID) {
            return (
                user.Professionals.find(
                    (professional) => professional.ID === professionalID,
                ) ?? user
            );
        }
        return user;
    }, [professionalID, user]);
    const isCompanyInbox = selectedInbox.__typename === 'MemberProfessional';

    function getUnreadCount(professionalID: number | null): number {
        if (professionalID) {
            return (
                notifications.companyInboxes.find(
                    (inbox) => inbox.professionalID === professionalID,
                )?.count ?? 0
            );
        }
        return notifications.personalInbox.count;
    }

    return (
        <div className={cn(className)}>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'flex h-12 w-full items-center gap-3 rounded-full px-4 text-left hover:bg-gray-200',
                            notifications.totalUnreadCount -
                                getUnreadCount(professionalID) >
                                0 && 'bg-gray-100',
                        )}
                    >
                        <span className="min-w-0 flex-1 truncate text-4 font-medium">
                            {isCompanyInbox
                                ? selectedInbox.Title
                                : 'Personal Inbox'}
                        </span>
                        <NotificationBadge
                            count={getUnreadCount(professionalID)}
                        />
                        <ChevronDownIcon className="size-4 shrink-0 text-black/70" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className="w-[calc(var(--radix-dropdown-menu-trigger-width))]"
                    side="bottom"
                    align="start"
                >
                    <DropdownMenuItem
                        className="flex items-center gap-3 focus:bg-black/10"
                        onClick={() => onInboxChange?.(null)}
                    >
                        <ParticipantAvatar
                            className="size-8"
                            fallbackClassName="!text-4"
                            participant={user}
                        />
                        <span className="min-w-0 flex-1 truncate">
                            Personal Inbox
                        </span>
                        <NotificationBadge count={getUnreadCount(null)} />
                    </DropdownMenuItem>
                    {companyInboxes.map((professional) => (
                        <DropdownMenuItem
                            key={professional.ID}
                            className="flex items-center gap-3 focus:bg-black/10"
                            onClick={() => onInboxChange?.(professional.ID)}
                        >
                            <ParticipantAvatar
                                className="size-8"
                                fallbackClassName="!text-4"
                                participant={professional}
                            />
                            <span className="min-w-0 flex-1 truncate">
                                {professional.Title}
                            </span>
                            <NotificationBadge
                                count={getUnreadCount(professional.ID)}
                            />
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
